import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Recycle, Calendar, AlertCircle, CheckCircle2, Clock,
  ArrowRight, ChevronRight, Package, TrendingDown, RefreshCw, FileText
} from 'lucide-react';

const timeline = [
  {
    year: '2025',
    date: '2025 vasario 11 d.',
    status: 'completed',
    title: 'PPWR įsigaliojo',
    desc: 'ES Pakuočių ir pakuočių atliekų reglamentas (PPWR) oficialiai įsigaliojo. Pakeičia senąją Direktyvą 94/62/EB.',
    items: ['Reglamentas paskelbtas ES Oficialiajame leidinyje (2025-01-22)', 'Įsigaliojimas – 20 dienų po paskelbimo', 'Taikomas tiesiogiai visose ES valstybėse narėse, įskaitant Lietuvą']
  },
  {
    year: '2026',
    date: '2026 rugpjūčio 12 d.',
    status: 'upcoming',
    title: 'Privalomas taikymas',
    desc: 'Baigiantis 18 mėnesių pereinamajam laikotarpiui, dauguma PPWR nuostatų tampa privalomos visoms įmonėms.',
    items: [
      'Privalomos pakuočių projektavimo ir sudėties taisyklės',
      'Draudimas naudoti PFAS maisto kontaktinėse pakuotėse',
      'Ribojamas sunkiųjų metalų (švino, kadmio, gyvsidabrio) kiekis',
      'Prasideda išplėstinės gamintojo atsakomybės (EPR) sistema'
    ]
  },
  {
    year: '2029',
    date: '2029 sausio 1 d.',
    status: 'future',
    title: 'Atskiros surinkimo infrastruktūros terminas',
    desc: 'Visose valstybėse narėse turi būti sukurta grąžinimo ir atskiros surinkimo sistema stiklui, metalams ir plastikams.',
    items: [
      'Stiklo, metalų, plastikų atskiras surinkimas',
      'Lietuva privalo atnaujinti atliekų tvarkymo infrastruktūrą',
      'Užstato sistemos išplėtimas'
    ]
  },
  {
    year: '2030',
    date: '2030 sausio 1 d.',
    status: 'future',
    title: 'Perdirbimo ir daugkartinio naudojimo tikslai',
    desc: 'Kertiniai pakuočių perdirbimo ir atliekų mažinimo tikslai.',
    items: [
      'Visos ES pakuotės turi būti perdirbamos (A–C klasė)',
      '65% visų pakuočių atliekų turi būti perdirbta',
      'Pakuočių atliekos sumažintos 5% vienam gyventojui (lyginant su 2018 m.)',
      'Minimalus perdirbtų medžiagų kiekis plastikinėse pakuotėse',
      'Bent 40% transporto ir pardavimo pakuočių – daugkartinės'
    ]
  },
  {
    year: '2035',
    date: '2035 m.',
    status: 'future',
    title: 'Aukštesni perdirbimo standartai',
    desc: 'Perdirbamumas turi būti įrodytas praktiškai veikiančioje infrastruktūroje.',
    items: [
      'Tik A ir B klasės pakuotės leidžiamos rinkoje',
      'Pakuočių atliekos sumažintos 10% vienam gyventojui',
      'Perdirbimas įrodomas realios infrastruktūros veikla (ne tik teoriškai)'
    ]
  },
  {
    year: '2038',
    date: '2038 m.',
    status: 'future',
    title: 'Griežtesni perdirbimo reikalavimai',
    desc: 'Leidžiamos tik aukščiausio lygio pakuotės.',
    items: [
      'Rinkoje leidžiamos tik A ir B klasės pakuotės',
      'C klasė nebegalima'
    ]
  },
  {
    year: '2040',
    date: '2040 m.',
    status: 'future',
    title: 'Galutinis pakuočių atliekų tikslas',
    desc: 'Europos Žaliasis kursas – maksimalus atliekų mažinimas.',
    items: [
      'Pakuočių atliekos sumažintos 15% vienam gyventojui (lyginant su 2018 m.)',
      '70% pakuočių – daugkartinės'
    ]
  }
];

const statusConfig = {
  completed: { color: 'bg-green-500', light: 'bg-green-50 border-green-200', icon: CheckCircle2, iconColor: 'text-green-600', badge: 'bg-green-100 text-green-800', badgeText: 'Įvykdyta' },
  upcoming: { color: 'bg-orange-400', light: 'bg-orange-50 border-orange-200', icon: AlertCircle, iconColor: 'text-orange-500', badge: 'bg-orange-100 text-orange-800', badgeText: 'Artėja' },
  future: { color: 'bg-gray-300', light: 'bg-gray-50 border-gray-200', icon: Clock, iconColor: 'text-gray-400', badge: 'bg-gray-100 text-gray-600', badgeText: 'Ateitis' }
};

const PPWRPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-[#00C300]" />
            <span className="text-lg font-bold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-semibold text-gray-600 hover:text-[#00C300] transition-colors">Pradžia</Link>
            <Link to="/ppwr" className="text-sm font-semibold text-[#00C300] border-b-2 border-[#00C300] pb-0.5">PPWR</Link>
            <Link to="/login" className="text-sm font-semibold text-gray-600 hover:text-[#00C300] transition-colors">Skaičiuotuvas</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full font-semibold text-gray-700 hover:text-[#00C300]">Prisijungti</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-6 font-semibold">Registruotis</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-white border-b border-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <Package className="w-4 h-4" />
              ES Reglamentas (EU) 2025/40
            </div>
            <h1 className="text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
              PPWR – Pakuočių ir pakuočių atliekų reglamentas
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Naujas ES reglamentas, pakeičiantis 30 metų galiojusią Direktyvą 94/62/EB. Sužinokite, kaip ir kada PPWR paveiks jūsų verslą Lietuvoje.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button className="bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-8 font-bold h-12">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Patikrinti savo įsipareigojimus
                </Button>
              </Link>
              <a href="#timeline">
                <Button variant="outline" className="rounded-full px-8 font-semibold h-12 border-2 border-gray-200 hover:border-[#00C300] hover:text-[#00C300]">
                  Peržiūrėti terminus
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key facts */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Calendar, color: 'bg-green-100 text-[#00C300]', label: 'Įsigaliojo', value: '2025-02-11' },
              { icon: AlertCircle, color: 'bg-orange-100 text-orange-600', label: 'Privalomas nuo', value: '2026-08-12' },
              { icon: Package, color: 'bg-blue-100 text-blue-600', label: 'Pakeičia', value: 'Dir. 94/62/EB' },
              { icon: TrendingDown, color: 'bg-purple-100 text-purple-600', label: 'Mažinimo tikslas iki 2040', value: '-15% atliekų' },
            ].map(({ icon: Icon, color, label, value }) => (
              <Card key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
                    <p className="text-lg font-extrabold text-gray-900">{value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What is PPWR */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-extrabold text-gray-900">Kas yra PPWR?</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                <strong className="text-gray-900">Packaging and Packaging Waste Regulation (PPWR)</strong> – tai naujas tiesiogiai taikomas ES reglamentas, pakeičiantis Direktyvą 94/62/EB, kuri reguliavo pakuočių atliekas nuo 1994 m.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Skirtingai nuo direktyvos, reglamentas <strong>tiesiogiai taikomas</strong> visose ES valstybėse narėse be atskiro nacionalinio įgyvendinimo. Tai reiškia, kad Lietuva ir lietuviškos įmonės privalo laikytis reglamento nuo 2026 m. rugpjūčio 12 d.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Perdirbamumas', desc: 'Visos pakuotės turi būti projektuojamos taip, kad jas būtų galima perdirbti' },
                  { title: 'Daugkartinis naudojimas', desc: 'Privalomi tikslai kiek pakuočių turi būti daugkartinės' },
                  { title: 'Perdirbtų medžiagų kiekis', desc: 'Minimalūs perdirbtų žaliavų reikalavimai plastikinėms pakuotėms' },
                  { title: 'Atliekų mažinimas', desc: 'Privalomi tikslai sumažinti pakuočių atliekas vienam gyventojui' },
                ].map(({ title, desc }) => (
                  <div key={title} className="flex items-start gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                    <CheckCircle2 className="w-5 h-5 text-[#00C300] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-gray-900">{title}</p>
                      <p className="text-gray-600 text-sm">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                PPWR vs Direktyva 94/62/EB
              </h3>
              <div className="space-y-4">
                {[
                  { aspect: 'Taikymas', old: 'Per nacionalinį įstatymą', new: 'Tiesiogiai, be nacionalinio įgyvendinimo' },
                  { aspect: 'Perdirbamumas', old: 'Rekomendacinis', new: 'Privalomas visoms pakuotėms' },
                  { aspect: 'Daugkartinis naud.', old: 'Nereglamentuota', new: 'Privalomi tikslai (40% iki 2030)' },
                  { aspect: 'Perdirbtų med.', old: 'Nereglamentuota', new: 'Minimalūs % plastikams' },
                  { aspect: 'Atliekų mažinimas', old: 'Tik perdirbimo tikslai', new: 'Tikslai vienam gyventojui' },
                ].map(({ aspect, old, new: newVal }) => (
                  <div key={aspect} className="bg-white rounded-xl p-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{aspect}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-red-50 rounded-lg p-2">
                        <p className="text-xs text-red-500 font-semibold mb-1">Senoji dir.</p>
                        <p className="text-gray-700">{old}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2">
                        <p className="text-xs text-[#00C300] font-semibold mb-1">PPWR</p>
                        <p className="text-gray-700">{newVal}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section id="timeline" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Įgyvendinimo terminai Lietuvoje</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              PPWR taikomas tiesiogiai – Lietuvai nereikia atskiro nacionalinio įstatymo. Svarbiausios datos:
            </p>
          </div>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />
            <div className="space-y-6">
              {timeline.map((item) => {
                const cfg = statusConfig[item.status];
                const Icon = cfg.icon;
                return (
                  <div key={item.year} className="relative flex gap-6 md:gap-10">
                    {/* Dot */}
                    <div className="hidden md:flex flex-col items-center">
                      <div className={`w-5 h-5 ${cfg.color} rounded-full border-4 border-white shadow-md z-10 mt-6`} />
                    </div>
                    {/* Card */}
                    <Card className={`flex-1 rounded-2xl border ${cfg.light} shadow-sm`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-2xl font-extrabold text-gray-900">{item.year}</span>
                              <span className={`text-xs font-bold px-3 py-1 rounded-full ${cfg.badge}`}>{cfg.badgeText}</span>
                            </div>
                            <p className="text-sm text-gray-500 font-medium">{item.date}</p>
                          </div>
                          <Icon className={`w-6 h-6 ${cfg.iconColor} flex-shrink-0 mt-1`} />
                        </div>
                        <CardTitle className="text-xl font-bold text-gray-900 mt-2">{item.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-gray-600 mb-4 leading-relaxed">{item.desc}</p>
                        <ul className="space-y-2">
                          {item.items.map((i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                              <ChevronRight className="w-4 h-4 text-[#00C300] flex-shrink-0 mt-0.5" />
                              {i}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Impact on business */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Ką PPWR reiškia jūsų verslui?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Įmonės, tiekiančios pakuotas prekes į ES rinką, turi pasirengti šiems reikalavimams</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Package, color: 'bg-blue-100 text-blue-600', title: 'Pakuočių perprojektavimas',
                desc: 'Pakuotės turi būti perdirbamos (A–C klasė iki 2030 m., tik A–B iki 2038 m.). Reikia įvertinti dabartines pakuotes ir jas adaptuoti.'
              },
              {
                icon: RefreshCw, color: 'bg-green-100 text-[#00C300]', title: 'Daugkartinis naudojimas',
                desc: 'Nuo 2030 m. 40% transporto ir pardavimo pakuočių turi būti daugkartinės. Verslas turi kurti grąžinimo sistemas.'
              },
              {
                icon: FileText, color: 'bg-purple-100 text-purple-600', title: 'Ataskaitos ir registracija',
                desc: 'EPR sistema reikalauja registracijos, metinių ataskaitų ir mokesčių pakuočių tvarkymui finansuoti. Taršos mokesčio apskaita tampa dar svarbesnė.'
              },
            ].map(({ icon: Icon, color, title, desc }) => (
              <Card key={title} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center mb-4`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-xl font-bold">{title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#00C300]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">Pasirenkite PPWR reikalavimams jau dabar</h2>
          <p className="text-green-50 text-lg mb-8 max-w-2xl mx-auto">
            Naudokite mūsų skaičiuotuvą, kad įvertintumėte dabartinę taršos mokesčio naštą ir palygintumėte pakuočių tvarkymo organizacijas.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-[#00C300] hover:bg-gray-50 rounded-full px-10 font-bold h-14 text-lg shadow-lg">
              <RefreshCw className="w-5 h-5 mr-2" />
              Pradėti skaičiuoti nemokamai
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Recycle className="w-7 h-7 text-[#00C300]" />
                <span className="text-white font-bold text-lg">taršosmokestis.lt</span>
              </div>
              <p className="text-gray-400 leading-relaxed">Informacijos portalas apie pakuočių atliekų apskaitą ir taršos mokestį Lietuvoje.</p>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Nuorodos</h3>
              <ul className="space-y-3">
                <li><Link to="/" className="hover:text-[#00C300] transition-colors">Pradžia</Link></li>
                <li><Link to="/ppwr" className="hover:text-[#00C300] transition-colors">PPWR</Link></li>
                <li><Link to="/register" className="hover:text-[#00C300] transition-colors">Registracija</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Šaltiniai</h3>
              <ul className="space-y-3 text-sm">
                <li><a href="https://eur-lex.europa.eu" target="_blank" rel="noopener noreferrer" className="hover:text-[#00C300] transition-colors">EUR-Lex</a></li>
                <li><a href="https://environment.ec.europa.eu" target="_blank" rel="noopener noreferrer" className="hover:text-[#00C300] transition-colors">EC Environment</a></li>
                <li><a href="https://am.lrv.lt" target="_blank" rel="noopener noreferrer" className="hover:text-[#00C300] transition-colors">LR Aplinkos ministerija</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} taršosmokestis.lt – Informacija pateikta remiantis oficialiais ES šaltiniais
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PPWRPage;
